import {IValue} from "platformed-client/types";
import {Dispatch, SetStateAction, ComponentType, ReactNode} from "react";
import {TSchema, StaticDecode} from "typebox";
import {AnyFilter, Condition, FilterFromParts, FilterEntries} from "./filterState";
import {registerFilterConfigKey} from "./configKeys";

/*

The types in this file are used to define what filters are available, how
they behave, and how they are rendered.

*/

// The props passed to a filter chip component.
export type FilterChipProps<
  // The filter type supported by this chip.
  Filter extends AnyFilter,
  // Additional context required by the filter chip.
  Context extends object = object,
> = {filter: Filter; context: Context};

// The props passed to a filter picker component.
export type FilterPickerProps<
  // The filter type supported by this picker.
  Filter extends AnyFilter,
  // The type of values expected in the facet array.
  FacetValue extends IValue = null,
  // Additional context required by the filter picker.
  Context extends object = object,
> = {
  facet: TypedFacetCount<FacetValue>[];
  filter: Filter;
  onApply: Dispatch<SetStateAction<Filter>>;
  onClose: () => void;
  context: Context;
};

// A facet count with a strongly typed value.
export type TypedFacetCount<V extends IValue> = {value: V; count: number};

// The components used to render and manage a filter.
export type FilterComponents<
  // The filter type supported by these components.
  Filter extends AnyFilter,
  // The type of values expected in the facet array.
  FacetValue extends IValue = null,
  // Additional context required by the filter components.
  Context extends object = object,
> = {Chip: ComponentType<FilterChipProps<Filter, Context>>} &
  // Either a Picker component or an instant filter component must be provided.
  (
    | {Picker?: ComponentType<FilterPickerProps<Filter, FacetValue, Context>>; filter?: never}
    | {Picker?: never; filter: Filter}
  );
export type AnyFilterComponents = FilterComponents<any, any, any>;

// A filter definition describes a single filter type.
export type FilterDefinition<
  // The type of the schema used for serializing/deserializing the filter's value.
  ValueSchema extends TSchema,
  // The set of condition values supported by this filter.
  ConditionValue extends string,
  // The type of values expected in the facet array.
  FacetValue extends IValue,
  // Additional context required by the filter's chip and picker components.
  Context extends object,
  // Whether faceting is enabled for this filter.
  EnableFacet extends boolean,
  // The type of the filter's value (should not need to be overridden).
  FilterValue = StaticDecode<ValueSchema>,
> = {
  label: ReactNode;
  icon: ReactNode;
  schema: ValueSchema;
  conditions: readonly Condition<ConditionValue, FilterValue>[];
  facet: EnableFacet;
  components: FilterComponents<FilterFromParts<FilterValue, ConditionValue>, FacetValue, Context>;
};
export type AnyFilterDefinition = FilterDefinition<any, any, any, any, any, any>;

// A record of filter definitions keyed by their unique string identifiers.
export type AnyFilterDefinitions = Record<string, AnyFilterDefinition>;

// The full filter configuration, including all filter definitions.
export type FilterConfig<Ds extends AnyFilterDefinitions> = {
  key: string;
  filters: Ds;
  defaultFilters?: FilterEntries<Ds>;
};

// Extracts the context type required by a filter definition.
export type FilterContext<D extends AnyFilterDefinition> =
  D extends FilterDefinition<any, any, any, infer C, any, any> ? C : object;

// Combines the contexts required by all filter definitions into a single type.
type UnionToIntersection<U> = (U extends any ? (arg: U) => void : never) extends (arg: infer I) => void ? I : never;
export type CombinedFilterContext<Ds extends AnyFilterDefinitions> = UnionToIntersection<
  {
    [K in keyof Ds]: FilterContext<Ds[K]>;
  }[keyof Ds]
>;

// Function to create and register a filter configuration.
export function createFilters<const Ds extends AnyFilterDefinitions>(config: FilterConfig<Ds>) {
  registerFilterConfigKey(config.key);
  return config;
}

// Function to create and typecheck a filter definition.
export function createFilter<
  S extends TSchema,
  const Cs extends string,
  const V extends IValue,
  C extends object,
  EnableFacet extends boolean,
>(definition: FilterDefinition<S, Cs, V, C, EnableFacet>): FilterDefinition<S, Cs, V, C, EnableFacet> {
  return definition;
}
